<template>
  <div
    :class="[
      $style.base,
      {
        [$style.zebra]: zebra,
        [$style.hasActions]: hasActions
      }
    ]"
  >
    <div :class="$style.tableWrap">
      <BaseScroll>
        <div
          :class="$style.table"
          v-test="'_base-table'"
        >
          <RowComponent
            v-if="hasHeader"
            :cells="headers"
            :sortOrder="sortOrder"
            :selectable="selectable"
            :printCompact="printCompact"
            isHeader
            @sort="$emit('sort', $event)"
            v-test="'_base-table-header'"
          />
          <TransitionGroup :name="animateWithPagination ? 'list-fade' : ''">
            <RowComponent
              v-for="(row, index) in rows"
              :key="`row-${index}`"
              v-bind="row"
              :zebra="zebra"
              :disabled="row.disabled"
              :selectable="selectable"
              :selected="modelValue && row.id && modelValue.includes(row.id)"
              :breakLines="row.breakLines"
              :printCompact="printCompact"
              @select="onRowSelect(row.id, $event)"
              @click="$emit('select', row.id)"
              @clickCell="$emit('clickCell', row.id)"
              @valueChange="(value: any) => $emit('valueChange', {
                value,
                id: row.id
              })"
              v-test="'_base-table-row'"
            />
            <RowComponent
              v-for="(footer, index) in multipleFooterRows"
              :key="`footer-${index}`"
              :cells="footer"
              :printCompact="printCompact"
              isFooter
              v-test="'_base-table-footer'"
            />
            <RowComponent
              v-if="singleFooterRow"
              :cells="singleFooterRow"
              :printCompact="printCompact"
              key="footer-row"
              isFooter
              v-test="'_base-table-footer'"
            />
          </TransitionGroup>
        </div>
      </BaseScroll>
      <div
        v-if="hasActions"
        :class="$style.actions"
      >
        <Actions
          :rows="rows"
          :zebra="zebra"
          :hasHeader="hasHeader"
          :hasFooter="hasFooter"
          @action="$emit('action', $event)"
        />
      </div>
    </div>
    <Flex
      v-if="showEndOfPage"
      direction="column"
      align="center"
      :class="$style.endOfPage"
      v-test="'_base-table-end-of-page'"
    >
      <BaseIcon
        name="arrow-up"
        size="l"
        @click="scrollToTop"
        color="primary"
      />
    </Flex>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, inject, ref } from 'vue';
import { scrollPage } from '@/helpers/scroll';
import { usePageLayoutStore } from '@/stores/page-layout';
import Actions from './Actions.vue';
import RowComponent from './Row.vue';
import type { Cell, Row } from './types';

const allDataFetched = inject<Ref<boolean>>(
  'paginationAllDataFetched',
  ref(false),
);
const perPage = inject<number>('paginationPerPage', 0);

type ModelValue = (number | string)[];

defineOptions({
  name: 'BaseTable',
  inheritAttrs: false,
});

interface Props {
  modelValue?: ModelValue;
  rows: Row[];
  headers: (Cell | string)[];
  footers?: (Cell | string)[] | (Cell | string)[][];
  disableZebra?: boolean;
  sortOrder?: Record<string, unknown>;
  printCompact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  headers: () => [],
  footers: () => [],
  disableZebra: false,
  printCompact: false,
});

const emit = defineEmits<{
  (e: 'select', ...args: any[]): void;
  (e: 'action', ...args: any[]): void;
  (e: 'valueChange', ...args: any[]): void;
  (e: 'clickCell', ...args: any[]): void;
  (e: 'sort', ...args: any[]): void;
  (e: 'update:modelValue', value: ModelValue): void;
}>();

const hasActions = computed(() => !!props.rows.find((row) => !!row.actions));
const hasHeader = computed(() => !!props.headers.length);
const hasFooter = computed(() => !!props.footers.length);
const zebra = computed(() => !props.disableZebra);
const selectable = computed(() => !!props.modelValue);

const hasMultipleFooters = computed(() => {
  return (
    Array.isArray(props.footers) &&
    props.footers.some((footer) => Array.isArray(footer))
  );
});

const multipleFooterRows = computed(() =>
  hasMultipleFooters.value ? (props.footers as (Cell | string)[][]) : [],
);

const singleFooterRow = computed(() =>
  hasFooter.value && !hasMultipleFooters.value
    ? (props.footers as (Cell | string)[])
    : null,
);

const onRowSelect = (id: number | string, value: boolean) => {
  const current = props.modelValue ?? [];
  let newValue: ModelValue = [...current];

  if (value) {
    newValue.push(id);
  } else {
    newValue = newValue.filter((rowId) => rowId !== id);
  }

  emit('update:modelValue', newValue);
};

const rowsExceedsPerPage = computed(() => props.rows.length > perPage);
const animateWithPagination = computed(
  () => perPage && rowsExceedsPerPage.value,
);
const showEndOfPage = computed(
  () => allDataFetched.value && rowsExceedsPerPage.value,
);

const scrollToTop = () => {
  const { screenSize } = usePageLayoutStore();

  if (screenSize === 's') {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    scrollPage({
      position: 'top',
      instant: false,
    });
  }
};
</script>

<style
  lang="scss"
  module
>
$actionsWidth: 52px;

.base {
  position: relative;
}

.tableWrap {
  width: 100%;
}

.table {
  display: table;
  width: 100%;

  .base.hasActions & {
    padding-right: $actionsWidth;
  }
}

.actions {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: $actionsWidth;
  z-index: 1;
}

.endOfPage {
  margin-top: $spacing * 2;
}
</style>
